import { t as translate } from "@magic-md/i18n";

export type PreviewWidthPreset = "fill" | "wide" | "work" | "reading" | "compact";
export type PreviewWidthControl = PreviewWidthPreset | "custom";
export type LegacyPreviewWidth = "full" | "prose" | "3xl" | "5xl";
export type CustomPreviewWidth = `custom:${number}`;
export type PreviewWidth = PreviewWidthPreset | LegacyPreviewWidth | CustomPreviewWidth;

export type PreviewWidthPresetConfig = {
    id: PreviewWidthPreset;
    label: string;
    width: number | "fill";
};

function tr(key: string, params?: Record<string, unknown>): string {
    return translate("editor", key, params);
}

export const PREVIEW_WIDTH_PRESETS: PreviewWidthPresetConfig[] = [
    { id: "fill", get label() { return tr("settings.previewWidth.fill"); }, width: "fill" },
    { id: "wide", get label() { return tr("settings.previewWidth.wide"); }, width: 1440 },
    { id: "work", get label() { return tr("settings.previewWidth.work"); }, width: 1200 },
    { id: "reading", get label() { return tr("settings.previewWidth.reading"); }, width: 960 },
    { id: "compact", get label() { return tr("settings.previewWidth.compact"); }, width: 720 },
];

export const PREVIEW_HORIZONTAL_PADDING = 32;
export const MIN_PREVIEW_VIEWPORT_WIDTH = 520;

const PREVIEW_WIDTH_PRESET_IDS = new Set<PreviewWidthPreset>(
    PREVIEW_WIDTH_PRESETS.map((preset) => preset.id),
);

const LEGACY_PREVIEW_WIDTH_MAP: Record<LegacyPreviewWidth, PreviewWidthPreset> = {
    full: "fill",
    prose: "reading",
    "5xl": "reading",
    "3xl": "compact",
};

function parseCustomPreviewWidth(value: string): number | null {
    if (!value.startsWith("custom:")) return null;
    const width = Number(value.slice("custom:".length));
    return Number.isFinite(width) && width > 0 ? Math.round(width) : null;
}

export function isPreviewWidthValue(value: unknown): value is PreviewWidth {
    if (typeof value !== "string") return false;
    if (PREVIEW_WIDTH_PRESET_IDS.has(value as PreviewWidthPreset)) return true;
    if (value in LEGACY_PREVIEW_WIDTH_MAP) return true;
    return parseCustomPreviewWidth(value) !== null;
}

export function normalizePreviewWidth(value: unknown): PreviewWidthPreset | CustomPreviewWidth {
    if (typeof value !== "string") return "fill";
    if (PREVIEW_WIDTH_PRESET_IDS.has(value as PreviewWidthPreset)) {
        return value as PreviewWidthPreset;
    }
    if (value in LEGACY_PREVIEW_WIDTH_MAP) {
        return LEGACY_PREVIEW_WIDTH_MAP[value as LegacyPreviewWidth];
    }
    const customWidth = parseCustomPreviewWidth(value);
    return customWidth ? createCustomPreviewWidth(customWidth) : "fill";
}

export function createCustomPreviewWidth(width: number): CustomPreviewWidth {
    return `custom:${Math.round(width)}` as CustomPreviewWidth;
}

export function splitPreviewWidthValue(value: unknown): {
    control: PreviewWidthControl;
    customWidth: number | null;
} {
    const normalized = normalizePreviewWidth(value);
    if (normalized.startsWith("custom:")) {
        return {
            control: "custom",
            customWidth: parseCustomPreviewWidth(normalized) ?? null,
        };
    }
    return {
        control: normalized as PreviewWidthPreset,
        customWidth: null,
    };
}

export function isPersistablePreviewWidthSetting(
    value: unknown,
): value is PreviewWidthPreset {
    return typeof value === "string" && PREVIEW_WIDTH_PRESET_IDS.has(value as PreviewWidthPreset);
}

export function previewWidthControlLabel(
    control: PreviewWidthControl,
    customWidth: number | null,
): string {
    if (control === "custom") {
        return customWidth
            ? tr("previewWidth.customWithPx", { width: Math.round(customWidth) })
            : tr("previewWidth.custom");
    }
    return PREVIEW_WIDTH_PRESETS.find((item) => item.id === control)?.label ?? tr("settings.previewWidth.fill");
}

export function clampPreviewViewportWidth(width: number, availableWidth: number): number {
    const minWidth = Math.min(MIN_PREVIEW_VIEWPORT_WIDTH, availableWidth);
    return Math.round(Math.min(availableWidth, Math.max(minWidth, width)));
}

export function resolvePreviewViewportWidth(
    control: PreviewWidthControl,
    customWidth: number | null,
    availableWidth: number | null,
): number | "fill" {
    if (!availableWidth) return "fill";
    if (control === "fill") return "fill";
    if (control === "custom") {
        return clampPreviewViewportWidth(customWidth ?? availableWidth, availableWidth);
    }
    const config = PREVIEW_WIDTH_PRESETS.find((item) => item.id === control);
    if (!config || config.width === "fill") return "fill";
    return clampPreviewViewportWidth(config.width, availableWidth);
}
